import React, { useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarInset } from "@repo/ui/primitives/sidebar";
import { InfoTriggerTargetContext } from "@repo/ui/primitives/info-trigger-context";
import { SISMSidebar } from "@/components/sidebar/Sidebar";
import { Header } from "@/components/layout/Header";
import { DashboardDock } from "@/components/layout/DashboardDock";
import { GlobalCommandCenter } from "@/components/layout/GlobalCommandCenter";
import { useAuthStore, useTenantStore } from "@/store";
import { useBreadcrumbStore } from "@/store/breadcrumb.store";
import { authIntegration } from "@/lib/api/client";
import { HelperTourOverlay } from "@/components/common/HelperTourOverlay";
import { useTranslation } from "react-i18next";
import { prefetchNamespacesForPath } from "@/lib/i18n-route-namespaces.js";
import { AccessLab } from "@/components/dev/access-lab/AccessLab";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useSidebarNavigation } from "@/hooks/useSidebarNavigation.js";
import { useUserNavigationPreferenceSync } from "@/hooks/useUserNavigationPreferenceSync.js";
import { buildBreadcrumb } from "@/lib/breadcrumb.js";
import {
    getDockPreferenceKey,
    resolveDockPreferences,
    useDockPreferencesStore,
} from "@/store/dock-preferences.store.js";
import {
    Home, Package, Shield, Activity, Lock, AlertTriangle,
    CheckCircle, Search, BarChart3, Building2, Ticket, Workflow, Users, Settings, Server, ShoppingCart, Table,
    LayoutDashboard, AlertCircle, WandSparkles, BookOpen
} from "lucide-react";

const MODULE_CONFIG = {
    'home': { icon: Home },
    'dashboard': { icon: LayoutDashboard },
    'asset-inventory-management': { icon: Package },
    'iso-27001': { icon: Shield },
    'isms-controls': { icon: Shield },
    'bcm': { icon: Activity },
    'gdpr-operations': { icon: Lock },
    'risk-management': { icon: AlertTriangle },
    'internal-control': { icon: CheckCircle },
    'audit-compliance': { icon: Search },
    'report-operations': { icon: BarChart3 },
    'context-management': { icon: Building2 },
    'ticketing': { icon: Ticket },
    'itsm': { icon: Workflow },
    'user-operations': { icon: Users },
    'definitions': { icon: Settings },
    'general-system': { icon: Settings },
    'admin': { icon: Server },
    'platform': { icon: Server },
    'incident-management': { icon: AlertCircle },
    'vendor-management': { icon: ShoppingCart },
    'bigdes-operations': { icon: Table },
    'my-work': { icon: CheckCircle },
    'setup': { icon: WandSparkles },
    'user-manual': { icon: BookOpen },
};

export function MainLayout() {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation("breadcrumb");
    const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
    const dynamicNames = useBreadcrumbStore((state) => state.dynamicNames);
    const dockPreferenceKey = useMemo(
        () => getDockPreferenceKey(user, selectedTenantId),
        [user, selectedTenantId],
    );
    const preferencesReady = useUserNavigationPreferenceSync(dockPreferenceKey);
    const navigation = useSidebarNavigation(dockPreferenceKey, { preferencesReady });
    const {
        data: sidebarData,
        allData,
        availableModules,
        activeWorkspace,
        customWorkspaces,
    } = navigation;
    const [commandCenterOpen, setCommandCenterOpen] = React.useState(false);
    const [breadcrumbInfoTarget, setBreadcrumbInfoTarget] = React.useState(null);

    const dockPages = useMemo(
        () => navigation.allPages.filter((page) => page.url !== "/home"),
        [navigation.allPages],
    );
    const storedDockPreferences = useDockPreferencesStore((state) =>
        dockPreferenceKey ? state.preferencesByScope[dockPreferenceKey] : undefined,
    );
    const dockPreferences = useMemo(
        () => resolveDockPreferences(storedDockPreferences),
        [storedDockPreferences],
    );

    const breadcrumb = useMemo(
        () => buildBreadcrumb({
            pathname: location.pathname,
            search: location.search,
            navMain: allData?.navMain,
            dynamicNames,
            t,
        }),
        [allData, dynamicNames, location.pathname, location.search, t],
    );

    const selectedTenantName = useMemo(() => {
        const memberships = user?.memberships;
        if (Array.isArray(memberships) && selectedTenantId) {
            return memberships.find((m) => m?.tenant?.id === selectedTenantId)?.tenant?.name;
        }
        return undefined;
    }, [user, selectedTenantId]);

    // Use provided title or derive from the last breadcrumb item
    const derivedTitle = (breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].name : "SISMS GRC");
    const fullTitle = selectedTenantName ? `${derivedTitle} - ${selectedTenantName}` : derivedTitle;
    usePageTitle(fullTitle);

    const handleLogout = async () => {
        await authIntegration.logout();
        navigate("/login");
    };

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const moduleKey = pathSegments[0] || 'home';
    const moduleInfo = MODULE_CONFIG[moduleKey] || { icon: Home };
    const isAboutSgrc = location.pathname === "/user-manual/about-sgrc";

    React.useEffect(() => {
        void prefetchNamespacesForPath(location.pathname);
    }, [location.pathname]);

    return (
        <SidebarProvider>
            {user && (
                <SISMSidebar
                    data={sidebarData}
                    allPages={navigation.allPages}
                    availableModules={availableModules}
                    activeWorkspace={activeWorkspace}
                    customWorkspaces={customWorkspaces}
                    favoritePages={navigation.favoritePages}
                    favoriteUrls={navigation.favoriteUrls}
                    navigationReady={Boolean(preferencesReady && navigation.labelsReady && navigation.preferenceScopeKey)}
                    onSelectWorkspace={navigation.selectWorkspace}
                />
            )}
            <SidebarInset className="relative flex h-dvh min-w-0 flex-col overflow-hidden bg-background">
                <Header
                    user={user}
                    onLogout={handleLogout}
                    breadcrumb={breadcrumb}
                    moduleIcon={moduleInfo.icon}
                    dockPages={dockPages}
                    dockPreferenceKey={dockPreferenceKey}
                    onOpenCommandCenter={() => setCommandCenterOpen(true)}
                    breadcrumbInfoRef={setBreadcrumbInfoTarget}
                />
                <GlobalCommandCenter
                    pages={navigation.allPages}
                    modules={availableModules}
                    customWorkspaces={customWorkspaces}
                    activeWorkspace={activeWorkspace}
                    preferenceScopeKey={dockPreferenceKey}
                    onSelectWorkspace={navigation.selectWorkspace}
                    open={commandCenterOpen}
                    onOpenChange={setCommandCenterOpen}
                />
                <HelperTourOverlay />
                <AccessLab onRefreshProfile={() => authIntegration.refreshProfileFromServer()} />
                <div className="flex flex-1 flex-col bg-background">
                    <div className={`app-shell-content overflow-y-auto overflow-x-hidden lg:peer-data-[state=collapsed]:overflow-hidden ${isAboutSgrc ? "p-0" : "px-[var(--ds-space-page-gutter-mobile)] pb-[var(--ds-space-8)] pt-[var(--ds-space-6)] sm:px-[var(--ds-space-page-gutter-compact)] lg:px-[var(--ds-space-page-gutter)]"}`}>
                        <div className="w-full min-w-0">
                            {/*Pass breadcrumb to children context*/}
                            <InfoTriggerTargetContext.Provider value={breadcrumbInfoTarget}>
                                <Outlet context={{ breadcrumb, navigation }} />
                            </InfoTriggerTargetContext.Provider>
                        </div>
                    </div>
                </div>
                <DashboardDock
                    pages={dockPages}
                    preferenceKey={dockPreferenceKey}
                    preferences={dockPreferences}
                />
            </SidebarInset>
        </SidebarProvider>
    );
}
